export type SalesKind = 'QUOTATION' | 'SALES_ORDER' | 'DELIVERY_RECEIPT' | 'SALES_INVOICE' | 'CREDIT_MEMO' | 'DEBIT_MEMO';
export type SalesStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'ISSUED' | 'CONVERTED' | 'CANCELLED';

export const KIND_LABEL: Record<SalesKind, string> = {
  QUOTATION: 'Quotation',
  SALES_ORDER: 'Sales Order',
  DELIVERY_RECEIPT: 'Delivery Receipt',
  SALES_INVOICE: 'Sales Invoice',
  CREDIT_MEMO: 'Credit Memo',
  DEBIT_MEMO: 'Debit Memo',
};

export const STATUS_LABEL: Record<SalesStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Awaiting approval',
  ISSUED: 'Issued',
  CONVERTED: 'Converted',
  CANCELLED: 'Cancelled',
};

export const STATUS_TONE = { DRAFT: 'neutral', PENDING_APPROVAL: 'warning', ISSUED: 'ok', CONVERTED: 'info', CANCELLED: 'critical' } as const;

export interface SalesSummary {
  id: string;
  kind: SalesKind;
  status: SalesStatus;
  number: string | null;
  serial: string | null;
  internalNumber: string | null;
  contactId: string;
  branchId: string;
  customer?: string;
  date: string;
  dueDate: string | null;
  reference: string;
  vatableSales: string;
  vatAmount: string;
  exemptSales: string;
  zeroRatedSales: string;
  governmentSales: string;
  scPwdDiscount: string;
  totalAmount: string;
  balanceDue: string;
  rowVersion: number;
}

export interface SalesLineView {
  id: string;
  lineNo: number;
  itemId: string | null;
  description: string;
  quantity: string;
  uom: string;
  unitPrice: string;
  discount: string;
  taxCode: string;
  accountId: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
  dimensions: Record<string, string>;
}

export interface SalesDetail extends Omit<SalesSummary, 'customer'> {
  notes: string;
  pricesIncludeVat: boolean;
  buyerName: string;
  buyerAddress: string;
  buyerTin: string;
  scPwdType: 'SENIOR_CITIZEN' | 'PWD' | null;
  scPwdIdNumber: string;
  scPwdName: string;
  journalEntryId: string | null;
  issuedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  printCount: number;
  branch: { id: string; code: string; name: string };
  customer: { registeredName: string; tinFormatted: string | null; address: string } & Record<string, unknown>;
  source: { id: string; kind: SalesKind; serial: string | null; internalNumber: string | null } | null;
  invoice: { id: string; serial: string | null; balanceDue: string } | null;
  derived: { id: string; kind: SalesKind; serial: string | null; internalNumber: string | null; status: SalesStatus }[];
  memos: { id: string; kind: SalesKind; serial: string | null; status: SalesStatus; totalAmount: string; date: string }[];
  receipts: { receiptId: string; number: string | null; date: string; amount: string; status: string }[];
  lines: SalesLineView[];
  warnings?: string[];
}

/** Which kinds each list screen shows. */
export const LIST_KINDS: Record<string, SalesKind[]> = {
  invoices: ['SALES_INVOICE'],
  quotations: ['QUOTATION', 'SALES_ORDER', 'DELIVERY_RECEIPT'],
  memos: ['CREDIT_MEMO', 'DEBIT_MEMO'],
};
